import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { applyTypography, RadioButton, useAuth, useT } from '@repo/common';
import PaymentSummary from '../../components/Kpathport/Booking/PaymentSummary';
import StepIndicator from '../../components/Kpathport/components/StepIndicator';
import RefundModal from '../../components/Kpathport/Modal/RefundModal';
import { ReactComponent as NavigationLeftIcon20 } from '@repo/common/assets/icons/20px/icon_20px_navigation-left.svg';
import {
  createBookings,
  cancelBooking,
  getBooking,
} from '../../api/booking/booking.api';
import { buildAllAnswersPayload } from '../../components/Kpathport/utils/intakeAnswers';
import {
  createPayment,
  createBankTransferPayment,
  getPaymentByBookingId,
  getPaymentByOrderId,
  prepareEximbayPayment,
  recordPolicyConsent,
} from '../../api/payment/payment.api';
import { getWorkshopDetail } from '../../api/workshop/workshop.api';
import { requestEximbayPayment } from '../../utils/eximbay';
import { isBankTransferMethod } from '../../components/Kpathport/utils/priceUtils';

const BOOKING_CACHE_TTL_MS = 5 * 60 * 1000;
const bookingCreateInFlight = new Map();

const normalizeCartItemIds = (cartItemIds = []) =>
  [
    ...new Set(
      cartItemIds.map((id) => parseInt(id, 10)).filter((id) => !isNaN(id)),
    ),
  ].sort((a, b) => a - b);

const getBookingCacheKey = (cartItemIds) =>
  `kpathport:bookings:${normalizeCartItemIds(cartItemIds).join(',')}`;

const readBookingCache = (cacheKey) => {
  try {
    const raw = sessionStorage.getItem(cacheKey);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed?.createdAt || !Array.isArray(parsed?.bookingIds)) return null;

    const isExpired = Date.now() - parsed.createdAt > BOOKING_CACHE_TTL_MS;
    if (isExpired) {
      sessionStorage.removeItem(cacheKey);
      return null;
    }

    return parsed.bookingIds;
  } catch (error) {
    sessionStorage.removeItem(cacheKey);
    return null;
  }
};

const writeBookingCache = (cacheKey, bookingIds) => {
  try {
    sessionStorage.setItem(
      cacheKey,
      JSON.stringify({
        createdAt: Date.now(),
        bookingIds,
      }),
    );
  } catch (error) {
    // ignore sessionStorage write failures
  }
};

const clearBookingCache = () => {
  try {
    Object.keys(sessionStorage)
      .filter((key) => key.startsWith('kpathport:bookings:'))
      .forEach((key) => sessionStorage.removeItem(key));
  } catch (error) {
    // ignore cache cleanup failures
  }
};

const isFreeBooking = (booking) => {
  const price = Number(booking?.price ?? 0);
  return Boolean(booking?.isFree) || price <= 0;
};

// 결제 대상 booking을 무료/유료로 분리한다.
// 무료는 이미 CONFIRMED 상태이므로 결제 호출에서 제외하고,
// 페이지 이탈 시 자동 취소 대상에서도 빼서 사용자가 손해를 보지 않게 한다.
// 유료 중 계좌이체 워크숍(booking.paymentMethod === 'BANK_TRANSFER')은 Eximbay 대신
// 계좌이체 단계로 보내야 하므로 따로 표시한다.
const splitFreeAndPaid = async (targetBookingIds) => {
  if (!Array.isArray(targetBookingIds) || targetBookingIds.length === 0) {
    return {
      paidIds: [],
      allFree: false,
      isBankTransfer: false,
      paidBookings: [],
    };
  }
  const bookings = await Promise.all(
    targetBookingIds.map((bookingId) => getBooking(bookingId)),
  );
  const paidBookings = bookings.filter((booking) => !isFreeBooking(booking));
  const paidIds = paidBookings.map((booking) => booking.id);
  const isBankTransfer =
    paidBookings.length > 0 &&
    paidBookings.every((booking) =>
      isBankTransferMethod(booking.paymentMethod),
    );
  return {
    paidIds,
    allFree: paidIds.length === 0,
    isBankTransfer,
    paidBookings,
  };
};

// 이미 계좌 안내 단계까지 간 예약이면 그 결제 레코드를 돌려준다 (없으면 null).
// 서버는 같은 슬롯의 결제 대기 예약을 새로 만들지 않고 기존 예약 ID 를 돌려주므로,
// 여기서 기존 결제가 있으면 결제 단계를 다시 밟지 않고 안내 페이지로 직행해야 한다.
// 결제 단계를 다시 그리면 이탈 시 자동 취소가 이미 입금 안내까지 받은 예약을 지워 버린다.
const findExistingBankTransferPayment = async (bookingId) => {
  if (!bookingId) return null;
  try {
    const payment = await getPaymentByBookingId(bookingId);
    const provider = String(payment?.paymentProvider || '').toUpperCase();
    const status = String(payment?.paymentStatus || '').toUpperCase();
    if (
      provider === 'BANK_TRANSFER' &&
      payment?.orderId &&
      ['READY', 'DONE'].includes(status)
    ) {
      return payment;
    }
  } catch (error) {
    // 결제 레코드가 없으면 404 — 정상적인 첫 진입
  }
  return null;
};

const Payments = () => {
  const t = useT();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [currentStep] = useState(3);
  const [selectedPayment, setSelectedPayment] = useState('eximbay');
  const [isProcessing, setIsProcessing] = useState(false);
  const [total, setTotal] = useState(0);
  // 결제 요약의 통화 — 카드 결제는 결제 레코드, 계좌이체는 예약의 통화를 따른다 (KRW 를 "$" 로 보이면 안 된다)
  const [currency, setCurrency] = useState('USD');
  const [bookingIds, setBookingIds] = useState([]);
  const [paymentData, setPaymentData] = useState(null);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [agreedToPolicy, setAgreedToPolicy] = useState(false);
  // 'card' — Eximbay. 'bank' — 계좌이체 워크숍(예: Prana Club 한옥 웰니스): PG 없이 안내 페이지로.
  const [paymentMode, setPaymentMode] = useState('card');
  const [bankWorkshop, setBankWorkshop] = useState(null);
  const [refundPolicyText, setRefundPolicyText] = useState('');
  const bookingIdsRef = useRef([]);
  const isProcessingRef = useRef(false);
  const shouldCancelOnExitRef = useRef(true);
  const paymentPreparedRef = useRef(false);

  useEffect(() => {
    bookingIdsRef.current = bookingIds;
  }, [bookingIds]);

  useEffect(() => {
    isProcessingRef.current = isProcessing;
  }, [isProcessing]);

  useEffect(() => {
    return () => {
      if (!shouldCancelOnExitRef.current) return;
      if (isProcessingRef.current) return;

      const ids = bookingIdsRef.current || [];
      if (ids.length > 0) {
        ids.forEach((bookingId) => {
          cancelBooking(
            bookingId,
            'User left payment page before completing payment',
          ).catch(() => {});
        });
      }
      clearBookingCache();
    };
  }, []);

  // 첫 예약의 워크숍 상세 — 워크숍 전용 환불 정책, 계좌이체 워크숍이면 계좌 안내에도 쓴다
  const loadWorkshopContext = useCallback(async (paidBookings) => {
    const first = paidBookings?.[0];
    if (!first?.workshopId) return null;
    try {
      const workshop = await getWorkshopDetail(first.workshopId);
      setRefundPolicyText((workshop?.refundPolicy || '').trim());
      return workshop;
    } catch (error) {
      console.error('Failed to load workshop for payment context:', error);
      return null;
    }
  }, []);

  // 계좌이체 단계로 전환. Eximbay 결제 레코드는 만들지 않는다.
  const enterBankTransferMode = useCallback(
    async (paidBookings) => {
      const sum = paidBookings.reduce(
        (acc, booking) => acc + Number(booking?.price ?? 0),
        0,
      );
      setPaymentMode('bank');
      setSelectedPayment('bank');
      setTotal(sum);
      setCurrency(paidBookings[0]?.currency || 'KRW');
      const workshop = await loadWorkshopContext(paidBookings);
      setBankWorkshop(workshop);
    },
    [loadWorkshopContext],
  );

  const redirectIfFreeBookings = useCallback(
    async (targetBookingIds = []) => {
      if (!Array.isArray(targetBookingIds) || targetBookingIds.length === 0) {
        return false;
      }

      const { allFree } = await splitFreeAndPaid(targetBookingIds);

      if (!allFree) return false;

      shouldCancelOnExitRef.current = false;
      clearBookingCache();
      navigate(
        `/community/kpathport/confirmed?bookingIds=${targetBookingIds.join(
          ',',
        )}`,
        { replace: true },
      );
      return true;
    },
    [navigate],
  );

  // 결제 준비 (단일 또는 다중 bookingId 지원)
  const preparePayment = useCallback(async (bookingIds) => {
    // 중복 호출 방지
    if (paymentPreparedRef.current) return;
    paymentPreparedRef.current = true;

    try {
      const ids = Array.isArray(bookingIds) ? bookingIds : [bookingIds];

      // 결제 생성 — customerEmail/Name은 백엔드가 인증 유저에서 직접 조회
      // (idempotency: 동일 bookingId에 대해 백엔드에서 중복 차단)
      const payment = await createPayment(ids);
      setPaymentData(payment);
      setTotal(parseFloat(payment.totalAmount) || 0);
      setCurrency(payment.currency || 'USD');
    } catch (error) {
      paymentPreparedRef.current = false;
      console.error('Failed to create payment:', error);
      throw error;
    }
  }, []);

  // 유료 예약 목록을 받아 카드/계좌이체 중 맞는 단계로 들어간다
  const startPaymentStep = useCallback(
    async (split) => {
      if (split.isBankTransfer) {
        const existing = await findExistingBankTransferPayment(split.paidIds[0]);
        if (existing) {
          shouldCancelOnExitRef.current = false;
          clearBookingCache();
          navigate(
            `/community/kpathport/bank-transfer?orderId=${existing.orderId}`,
            { replace: true },
          );
          return;
        }
        setBookingIds(split.paidIds);
        await enterBankTransferMode(split.paidBookings);
        return;
      }
      setBookingIds(split.paidIds);
      loadWorkshopContext(split.paidBookings);
      await preparePayment(split.paidIds);
    },
    [enterBankTransferMode, loadWorkshopContext, navigate, preparePayment],
  );

  // 장바구니에서 예약 생성 및 결제 준비
  const createBookingsAndPreparePayment = useCallback(
    async (cartItemIds) => {
      try {
        const normalizedCartItemIds = normalizeCartItemIds(cartItemIds);
        if (normalizedCartItemIds.length === 0) {
          alert(t('Invalid request.', '잘못된 요청입니다.'));
          navigate('/community/kpathport/booking');
          return;
        }

        const cacheKey = getBookingCacheKey(normalizedCartItemIds);
        // 사용자 정보 가져오기
        let customerName = '';
        let customerEmail = '';
        try {
          customerName =
            user?.firstName && user?.lastName
              ? `${user?.firstName} ${user?.lastName}`
              : user?.nickname || user?.email || '';
          customerEmail = user?.email || user?.socialEmail || '';
        } catch (error) {
          console.error('Failed to get user profile:', error);
        }

        // 예약 생성
        let createdBookingIds = readBookingCache(cacheKey);

        if (!createdBookingIds) {
          if (!bookingCreateInFlight.has(cacheKey)) {
            const createPromise = (async () => {
              const bookingIds = await createBookings(
                normalizedCartItemIds,
                customerName,
                customerEmail,
                // 어떤 워크숍이 섞였는지 여기선 알 수 없어 보관된 답변을 전부 보낸다.
                // 서버는 이번 예약에 해당하는 것만 쓰므로 남는 건 버려진다.
                // 여기서 지우지는 않는다 — 장바구니에 남은 다른 워크숍의 답변까지 날아간다.
                { answers: buildAllAnswersPayload() },
              );
              writeBookingCache(cacheKey, bookingIds);
              return bookingIds;
            })().finally(() => {
              bookingCreateInFlight.delete(cacheKey);
            });

            bookingCreateInFlight.set(cacheKey, createPromise);
          }

          createdBookingIds = await bookingCreateInFlight.get(cacheKey);
        }

        if (createdBookingIds.length === 0) {
          alert('예약 생성에 실패했습니다.');
          navigate('/community/kpathport/booking');
          return;
        }

        // 무료/유료를 분리한다. 무료가 섞여 있으면 무료는 이미 CONFIRMED 처리되었고
        // 결제 흐름에서는 유료 예약만 다뤄야 한다 (이탈 시 자동 취소도 유료만 대상).
        const split = await splitFreeAndPaid(createdBookingIds);

        if (split.allFree) {
          shouldCancelOnExitRef.current = false;
          clearBookingCache();
          navigate(
            `/community/kpathport/confirmed?bookingIds=${createdBookingIds.join(
              ',',
            )}`,
            { replace: true },
          );
          return;
        }

        // 유료 bookingId만 URL에 포함하여 결제 페이지 재진입 시 동일 예약 재사용
        navigate(
          `/community/kpathport/payments?bookingIds=${split.paidIds.join(',')}`,
          { replace: true },
        );
        await startPaymentStep(split);
      } catch (error) {
        console.error(
          'Failed to create bookings:',
          error?.response?.data || error,
        );
        throw error;
      }
    },
    [navigate, startPaymentStep, user, t],
  );

  // 초기화: 예약 생성 및 결제 준비
  useEffect(() => {
    const initializeBookingAndPayment = async () => {
      try {
        // 쿼리 파라미터에서 cartItemIds, bookingIds(복수), bookingId(단수) 가져오기
        const cartItemIdsParam = searchParams.get('cartItemIds');
        let bookingIdsParam = searchParams.get('bookingIds');
        const bookingIdParam = searchParams.get('bookingId');
        // 결제 실패 화면의 "다시 시도" 는 orderId 로 돌아온다 — 그 결제에 묶인 예약으로 이어서 진행한다
        const orderIdParam =
          searchParams.get('orderId') || searchParams.get('order_id');
        if (!bookingIdsParam && !bookingIdParam && !cartItemIdsParam && orderIdParam) {
          const payment = await getPaymentByOrderId(orderIdParam);
          const ids =
            payment?.bookingIds?.length
              ? payment.bookingIds
              : payment?.bookingId
                ? [payment.bookingId]
                : [];
          if (ids.length === 0) {
            alert(t('Invalid request.', '잘못된 요청입니다.'));
            navigate('/community/kpathport/booking');
            return;
          }
          bookingIdsParam = ids.join(',');
        }

        if (bookingIdsParam) {
          // 다중 예약 결제 (createBookingsAndPreparePayment가 설정한 URL)
          const ids = normalizeCartItemIds(
            bookingIdsParam.split(',').map((id) => parseInt(id.trim(), 10)),
          );
          if (ids.length === 0) {
            alert(t('Invalid request.', '잘못된 요청입니다.'));
            navigate('/community/kpathport/booking');
            return;
          }
          // 무료가 섞여 들어와도 안전하도록 분리한다.
          const split = await splitFreeAndPaid(ids);
          if (split.allFree) {
            shouldCancelOnExitRef.current = false;
            clearBookingCache();
            navigate(
              `/community/kpathport/confirmed?bookingIds=${ids.join(',')}`,
              { replace: true },
            );
            return;
          }
          await startPaymentStep(split);
        } else if (bookingIdParam) {
          // 단일 예약 (waitlist offer 등)
          const bookingId = parseInt(bookingIdParam, 10);
          if (isNaN(bookingId)) {
            alert(t('Invalid request.', '잘못된 요청입니다.'));
            navigate('/community/kpathport/booking');
            return;
          }
          const redirected = await redirectIfFreeBookings([bookingId]);
          if (redirected) return;
          const split = await splitFreeAndPaid([bookingId]);
          await startPaymentStep(split);
        } else if (cartItemIdsParam) {
          // 장바구니에서 예약 생성
          const cartItemIds = normalizeCartItemIds(
            cartItemIdsParam.split(',').map((id) => parseInt(id.trim(), 10)),
          );
          if (cartItemIds.length === 0) {
            alert('잘못된 요청입니다.');
            navigate('/community/kpathport/booking');
            return;
          }
          await createBookingsAndPreparePayment(cartItemIds);
        } else {
          alert('예약 정보가 없습니다.');
          navigate('/community/kpathport/booking');
          return;
        }
      } catch (error) {
        console.error('Failed to initialize booking and payment:', error);
        const serverMessage =
          error?.response?.data?.msg || error?.response?.data?.message;
        alert(
          serverMessage || '예약 생성에 실패했습니다. 다시 시도해주세요.',
        );
        navigate('/community/kpathport/booking');
      }
    };

    initializeBookingAndPayment();
  }, [
    searchParams,
    navigate,
    createBookingsAndPreparePayment,
    redirectIfFreeBookings,
    startPaymentStep,
    user,
    t,
  ]);

  const handleBack = async () => {
    if (isProcessing) return;

    if (bookingIds.length > 0) {
      await Promise.allSettled(
        bookingIds.map((bookingId) =>
          cancelBooking(bookingId, 'Canceled by Customer'),
        ),
      );
    }
    shouldCancelOnExitRef.current = false;
    clearBookingCache();
    navigate(-1);
  };

  // 계좌이체: 결제 레코드 생성 + 유저 안내 메일 + 주최측 알림 → 계좌 안내 페이지로.
  // 이 시점부터 HOLD(3시간)는 유저가 페이지를 떠나도 유지되어야 하므로 자동 취소를 끈다.
  const handleReserveWithBankTransfer = async () => {
    if (isProcessing || !agreedToPolicy || bookingIds.length === 0) return;
    setIsProcessing(true);
    try {
      const instruction = await createBankTransferPayment(bookingIds);
      shouldCancelOnExitRef.current = false;
      clearBookingCache();
      navigate(
        `/community/kpathport/bank-transfer?orderId=${instruction.orderId}`,
        { replace: true },
      );
    } catch (error) {
      console.error('Bank transfer initiation failed:', error);
      alert(
        error?.response?.data?.msg ||
          error?.response?.data?.message ||
          t(
            'Could not start the bank transfer. Please try again.',
            '계좌이체 안내를 만들지 못했습니다. 다시 시도해주세요.',
          ),
      );
      setIsProcessing(false);
    }
  };

  const handlePayNow = async () => {
    if (paymentMode === 'bank') {
      await handleReserveWithBankTransfer();
      return;
    }

    if (!paymentData) {
      alert('결제 시스템이 준비되지 않았습니다. 잠시 후 다시 시도해주세요.');
      return;
    }

    if (isProcessing || !agreedToPolicy) return;

    setIsProcessing(true);

    try {
      await recordPolicyConsent(paymentData.orderId);
      const request = await prepareEximbayPayment(paymentData.orderId);
      await requestEximbayPayment(request);
    } catch (error) {
      console.error('Eximbay payment request failed:', error);
      // 서버가 준 사유(통화 미지원, 예약 만료 등)를 먼저 보여 준다. axios 의 "status code 400" 은 아무 정보가 없다
      alert(
        error?.response?.data?.msg ||
          error?.response?.data?.message ||
          '결제 요청에 실패했습니다. 다시 시도해주세요.',
      );
      setIsProcessing(false);
    }
  };

  const isBankMode = paymentMode === 'bank';
  const bankInfo = bankWorkshop?.bankTransfer;

  return (
    <div className='content-start flex flex-wrap gap-[24px_0px] items-start justify-center pb-[240px] pl-[44px] pr-[100px] pt-[24px] relative size-full bg-grayscale-gray00 min-h-screen'>
      {/* Left Section */}
      <div className='content-start flex flex-[1_0_0] flex-wrap gap-0 items-start max-w-[778px] min-h-px min-w-[600px] relative'>
        {/* Back Button */}
        <div className='content-stretch flex h-[70px] items-center relative shrink-0'>
          <button
            onClick={handleBack}
            className='bg-grayscale-gray01 content-stretch flex items-center justify-center relative rounded-[50px] shrink-0 size-[36px] hover:opacity-80 transition-opacity'
            aria-label={t('Go back', '뒤로 가기')}
          >
            <div className='content-stretch flex items-center overflow-clip p-[2px] relative shrink-0 w-[20px]'>
              <NavigationLeftIcon20 className='w-full h-full' />
            </div>
          </button>
        </div>

        {/* Main Content */}
        <div className='content-stretch flex flex-[1_0_0] flex-col items-start min-h-px min-w-[520px] pl-[24px] relative'>
          {/* Header with Steps */}
          <div className='content-stretch flex h-[70px] items-center justify-between overflow-clip py-[16px] relative shrink-0 w-full'>
            <p
              className={`${applyTypography('h5')} text-text-primary shrink-0`}
              style={{ lineHeight: '1.2' }}
            >
              {t('Choose payment option', '결제 수단을 선택해 주세요')}
            </p>
            <StepIndicator currentStep={currentStep} />
          </div>

          {/* Payment Options */}
          <div className='content-stretch flex flex-col gap-[16px] items-start overflow-clip py-[24px] relative shrink-0 w-full'>
            {isBankMode ? (
              /* 계좌이체 워크숍: Eximbay 는 비활성(노출하지 않음), "Bank transfer" 한 가지만 선택된 상태로 보인다.
                 Pay now 를 누르면 계좌 안내 페이지(/bank-transfer)로 넘어간다. */
              <div className='bg-grayscale-gray01 border border-grayscale-gray02 border-solid content-stretch flex gap-[24px] items-center overflow-clip p-[24px] relative rounded-[15px] shrink-0 w-full'>
                <div className='content-stretch flex flex-[1_0_0] gap-[8px] items-center min-h-px min-w-px relative'>
                  <div className='relative shrink-0 size-[60px] rounded-[8px] flex items-center justify-center'>
                    <span className='text-green-deep02 font-bold text-xl'>₩</span>
                  </div>
                  <div className='content-stretch flex flex-[1_0_0] flex-col items-start min-h-px min-w-px relative'>
                    <p
                      className={`${applyTypography(
                        'b20Strong',
                      )} text-text-primary shrink-0 w-full whitespace-pre-wrap`}
                      style={{ lineHeight: '1.5' }}
                    >
                      {t('Bank transfer', '계좌이체')}
                    </p>
                    {bankInfo?.bankName && (
                      <p
                        className={`${applyTypography(
                          'b14',
                        )} text-text-secondary shrink-0 w-full`}
                        style={{ lineHeight: '1.5' }}
                      >
                        {bankInfo.bankName}
                        {bankInfo.accountHolder ? ` · ${bankInfo.accountHolder}` : ''}
                      </p>
                    )}
                  </div>
                </div>
                <RadioButton checked onChange={() => {}} />
              </div>
            ) : (
              /* Eximbay payment option */
              <div className='bg-grayscale-gray01 border border-grayscale-gray02 border-solid content-stretch flex gap-[24px] items-center overflow-clip p-[24px] relative rounded-[15px] shrink-0 w-full'>
                <div className='content-stretch flex flex-[1_0_0] gap-[8px] items-center min-h-px min-w-px relative'>
                  <div className='relative shrink-0 size-[60px] rounded-[8px] flex items-center justify-center'>
                    <span className='text-green-deep02 font-bold text-xl'>EX</span>
                  </div>
                  <div className='content-stretch flex flex-[1_0_0] flex-col items-start min-h-px min-w-px relative'>
                    <p
                      className={`${applyTypography(
                        'b20Strong',
                      )} text-text-primary shrink-0 w-full whitespace-pre-wrap`}
                      style={{ lineHeight: '1.5' }}
                    >
                      {t('Credit card · Eximbay', '신용카드 · Eximbay')}
                    </p>
                  </div>
                </div>
                <RadioButton
                  checked={selectedPayment === 'eximbay'}
                  onChange={() => setSelectedPayment('eximbay')}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Right Section - Payment Summary (동의 체크박스 포함) */}
      <div className='content-stretch flex flex-col gap-[24px] items-center pl-[24px] pt-[70px] relative shrink-0 w-[444px]'>
        <PaymentSummary
          total={total}
          currency={currency}
          onPayNow={handlePayNow}
          disabled={isBankMode ? bookingIds.length === 0 : !paymentData}
          loading={isProcessing}
          itemCount={bookingIds.length}
          onRefundDetails={() => setShowRefundModal(true)}
          requireAgreement
          agreed={agreedToPolicy}
          onAgreeChange={setAgreedToPolicy}
        />
      </div>

      <RefundModal
        isOpen={showRefundModal}
        onClose={() => setShowRefundModal(false)}
        content={refundPolicyText}
      />
    </div>
  );
};

export default Payments;
